/**
 * 選択素材 (GenerationAsset) の商品紐づきフラグ (hasProductLinks) を補う。
 *
 * 素材パネル・案件ページ・URL 保存の経路は一覧 / 保存 API の has_product_links を
 * 付けて selectedAssets に入れるが、手組みのドラッグ payload (シーンフレームのカルーセル・
 * ラストフレーム) は素材 ID しか持たず、`hasProductLinks` が undefined のまま届く。
 * undefined は useSelectedAssetBrands で「紐づき無し」に倒れ、ASIN の無いマニュアル案件に
 * 紐づく素材だけプロンプト入力モードの「AIに送る情報」(商品情報) トグルが出なくなる。
 *
 * ここでは不明なときだけ素材を 1 件取得し (GET /materials/temporary|cloud/<id> は
 * 一覧と同じ規則の has_product_links を返す)、asin / name / type の欠けも同時に補う。
 * 取得に失敗したら false (紐づきがあっても生成を止める側) に倒し、追加自体は止めない。
 */
import type { GenerationAsset } from '../../components/generation/state/types';
import { fetchMaterialById } from './api';
import type { CloudMaterial, TemporaryMaterial } from './materialsSlice';

export type MaterialFetcher = (materialId: string) => Promise<TemporaryMaterial | CloudMaterial | null>;

function hasAsin(asin: GenerationAsset['asin']): boolean {
  return Array.isArray(asin) ? asin.length > 0 : !!asin;
}

/** フラグが既に決まっているか (boolean で持っている、または ASIN から決まる) */
export function hasKnownProductLinkFlag(asset: Pick<GenerationAsset, 'hasProductLinks' | 'asin'>): boolean {
  return typeof asset.hasProductLinks === 'boolean' || hasAsin(asset.asin);
}

export async function ensureProductLinkFlag(
  asset: GenerationAsset,
  fetchMaterial: MaterialFetcher = fetchMaterialById,
): Promise<GenerationAsset> {
  if (typeof asset.hasProductLinks === 'boolean') return asset;
  if (hasAsin(asset.asin)) return { ...asset, hasProductLinks: true };

  let material: TemporaryMaterial | CloudMaterial | null = null;
  try {
    material = await fetchMaterial(asset.id);
  } catch {
    material = null;
  }
  if (!material) return { ...asset, hasProductLinks: false };

  const flag = (material as { has_product_links?: boolean }).has_product_links;
  const asin = asset.asin ?? material.asin ?? null;
  return {
    ...asset,
    asin,
    hasProductLinks: typeof flag === 'boolean' ? flag : hasAsin(asin),
    ...(!asset.name && material.name ? { name: material.name } : {}),
    // 手組み payload は置き場 (type) も欠けることがある。BE の folder_type で補う
    ...(!asset.type && material.folder_type ? { type: material.folder_type === 'cloud' ? 'cloud' : 'temporary' } : {}),
  };
}
